/* eslint-disable react-native/no-inline-styles */
/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CloseOutlined,
  InboxOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { paragraphMedium } from '../css'
import { Upload, Alert, Tabs, Modal, Tag } from 'antd'
import type { UploadProps } from 'antd'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { TextInput } from '../ts-components/TextInput'
import req, { getHeaders } from '@sportninja/common/api/request'
// @ts-ignore
import EnvHandler from '@sportninja/common/utils/env-handler'
import { getVideoUrl } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import {
  trackVideoUploadTabSwitched,
  trackVideoUploadStarted,
  trackVideoUploadCompleted,
  trackVideoUploadFailed,
  trackVideoUploadModalClosed,
} from 'src/utils/videoAnalytics'

const VIDEO_TYPE = {
  VIDEO_UPLOAD: '1',
  YOUTUBE_URL: '2',
  PIXELLOT: '3',
}

const TAB_LABELS: Record<string, string> = {
  [VIDEO_TYPE.VIDEO_UPLOAD]: 'video_upload',
  [VIDEO_TYPE.YOUTUBE_URL]: 'youtube_url',
  [VIDEO_TYPE.PIXELLOT]: 'pixellot',
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SECONDARY_100}`,
    borderTop: `1px solid ${colors.SECONDARY_100}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

const { Dragger } = Upload

interface UploadVideoModalProps {
  isOpen: boolean
  onClose: () => void
  gameId?: string | null
  readGame: (id: string | null) => void
  broadcastVideoUrl?: string
}

const UploadVideoModal: React.FC<UploadVideoModalProps> = ({
  isOpen,
  onClose,
  gameId = null,
  readGame = () => {},
  broadcastVideoUrl = '',
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { openModal, closeModal } = useModalScroll()

  const isEditing = broadcastVideoUrl !== null
  const isHostedOnS3 = broadcastVideoUrl?.includes('s3.amazonaws.com')

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string>(
    !isHostedOnS3 ? broadcastVideoUrl || '' : ''
  )

  const [authHeaders, setAuthHeaders] = useState<any>(null)
  const [authUrl, setAuthUrl] = useState<any>(null)
  const [activeTab, setActiveTab] = useState(
    !isHostedOnS3 && isEditing
      ? VIDEO_TYPE.YOUTUBE_URL
      : VIDEO_TYPE.VIDEO_UPLOAD
  )
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadFileName, setUploadFileName] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      openModal()
      // Reset state on open
      setError(null)
      setVideoUrl(!isHostedOnS3 ? broadcastVideoUrl || '' : '')
      setUploadSuccess(false)
      setUploadFileName(null)
      setActiveTab(
        !isHostedOnS3 && isEditing
          ? VIDEO_TYPE.YOUTUBE_URL
          : VIDEO_TYPE.VIDEO_UPLOAD
      )
    } else {
      closeModal()
      // Cleanup on close
      setError(null)
      setVideoUrl('')
    }
  }, [
    closeModal,
    isOpen,
    openModal,
    broadcastVideoUrl,
    isHostedOnS3,
    isEditing,
  ])

  useEffect(() => {
    const fetchAuthHeaders = async () => {
      const headers = await getHeaders(true)
      const auth = headers.get('authorization')
      setAuthHeaders(auth)
    }
    fetchAuthHeaders()
    EnvHandler.get().then((value) => {
      setAuthUrl(value)
    })
  }, [])

  const props: UploadProps = useMemo(
    () => ({
      accept: 'video/*',
      showUploadList: true,
      disabled: isLoading || uploadSuccess,
      name: 'video',
      multiple: false,
      action: `${authUrl}/games/${gameId}/upload`,
      headers: {
        Authorization: `${authHeaders}`,
      },
      beforeUpload() {
        setIsLoading(true)
        trackVideoUploadStarted(
          { id: gameId },
          {
            uploadMethod: 'file_upload',
          }
        )
      },
      onChange(info) {
        setError(null)
        setUploadSuccess(false)
        const { status } = info.file
        if (status === 'done') {
          setUploadFileName(info.file.name)
          setUploadSuccess(true)
          trackVideoUploadCompleted(
            { id: gameId },
            {
              uploadMethod: 'file_upload',
              fileName: info.file.name,
              isEditing,
            }
          )
        } else if (status === 'error') {
          let e = info?.file?.error?.message
            ? `An error occurred (${info.file.error.status}): ${info.file.error.message}`
            : 'An error occurred while uploading the file. (No message from server)'
          setError(e)
          setUploadSuccess(false)
          trackVideoUploadFailed(
            { id: gameId },
            {
              uploadMethod: 'file_upload',
              errorMessage: e,
            }
          )
        }
        setIsLoading(false)
      },
      onDrop() {},
    }),
    [isLoading, uploadSuccess, authUrl, gameId, authHeaders]
  )

  const tabs = useMemo(
    () => [
      {
        key: VIDEO_TYPE.VIDEO_UPLOAD,
        label: 'Upload Video',
        children: (
          <Dragger {...props}>
            <p className='ant-upload-drag-icon'>
              {uploadSuccess ? (
                <CheckCircleOutlined style={{ color: colors.GREEN_GOBLIN }} />
              ) : (
                <InboxOutlined />
              )}
            </p>
            {uploadFileName ? (
              <p
                css={css`
                  ${paragraphMedium}
                  color: ${colors.WHITE};
                  margin-bottom: 0;
                `}
              >
                {uploadFileName} uploaded successfully
              </p>
            ) : null}
            {!uploadSuccess ? (
              <>
                <p className='ant-upload-text'>
                  Click here to upload your video
                </p>
                <p className='ant-upload-hint'>
                  Supported formats: MP4, WEBM, OGG (up to 200 MB)
                </p>
              </>
            ) : null}
          </Dragger>
        ),
      },
      {
        key: VIDEO_TYPE.YOUTUBE_URL,
        label: 'YouTube (URL)',
        children: (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <TextInput
              name={'videoUrl'}
              value={videoUrl}
              setValue={(value) => setVideoUrl(value)}
              label='URL'
              type='text'
              customError={{}}
              fieldErrors={[]}
            />
          </div>
        ),
      },
      {
        key: VIDEO_TYPE.PIXELLOT,
        label: 'Pixellot',
        disabled: true,
        children: (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 150px;
            `}
          >
            <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
              Coming soon
            </Text>
          </div>
        ),
      },
    ],
    [props, uploadFileName, uploadSuccess, videoUrl]
  )

  const onChangeTabs = (key: string) => {
    trackVideoUploadTabSwitched(
      { id: gameId },
      {
        tab: TAB_LABELS[key] || key,
      }
    )
    setActiveTab(key)
  }

  const handleClose = useCallback(() => {
    trackVideoUploadModalClosed(
      { id: gameId },
      {
        wasUploadInProgress: isLoading,
        activeTab: TAB_LABELS[activeTab] || 'video_upload',
      }
    )
    onClose()
  }, [gameId, isLoading, activeTab, onClose])

  const isSaveDisabled = useCallback(() => {
    if (isLoading) {
      return true
    }
    if (activeTab === VIDEO_TYPE.VIDEO_UPLOAD) {
      return !uploadSuccess
    }
    if (activeTab === VIDEO_TYPE.YOUTUBE_URL) {
      return !videoUrl
    }
    return true
  }, [isLoading, activeTab, uploadSuccess, videoUrl])

  const handleClick = useCallback(async () => {
    try {
      setIsLoading(true)
      if (isSaveDisabled()) {
        return
      }
      let url = videoUrl
      // If that's a video upload
      // we just need to read the game and close the modal
      if (activeTab === VIDEO_TYPE.VIDEO_UPLOAD) {
        onClose()
        if (isEditing) {
          window.location.reload()
        } else {
          readGame(gameId)
        }
        return
      }
      // If that's a youtube video
      // we need to validate the URL
      // and update the game with the new video URL
      if (activeTab === VIDEO_TYPE.YOUTUBE_URL) {
        url = getVideoUrl(videoUrl)
        if (!url) {
          setError('Please enter a valid Youtube URL')
          return
        }
      }
      await req(`/games/${gameId}`, {
        method: 'PUT',
        body: JSON.stringify({
          broadcast_video_url: url,
        }),
      })
      trackVideoUploadCompleted(
        { id: gameId },
        {
          uploadMethod: 'youtube_url',
          isEditing,
        }
      )
      if (isEditing) {
        window.location.reload()
      } else {
        window.location.reload()
      }
      onClose()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [
    activeTab,
    isEditing,
    gameId,
    videoUrl,
    onClose,
    readGame,
    isSaveDisabled,
  ])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const getHeader = useCallback(() => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Add Livestream
        </Text>
        <Tag color='gold'>BETA</Tag>
      </div>
    )
  }, [])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
          ${isMobile &&
          css`
            width: 100%;
            flex-direction: column-reverse;
          `}
        `}
      >
        <Button
          text='Cancel'
          onClick={handleClose}
          variant='secondary'
          disabled={isLoading}
        />
        <Button
          text='Confirm'
          onClick={handleClick}
          variant='primary'
          isLoading={isLoading}
          disabled={isSaveDisabled()}
        />
      </div>
    )
  }, [isMobile, isLoading, handleClick, handleClose, isSaveDisabled])

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100%' : 'auto'} !important;
        max-height: ${isMobile ? '100%' : '90vh'};
        margin: ${isMobile ? '0' : '24px auto'};
        padding: 0;

        .ant-modal-content {
          height: ${isMobile ? '100%' : 'auto'} !important;
          max-height: ${isMobile ? '100%' : '90vh'};
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          min-height: 400px;
        }
        .ant-input {
          background-color: ${colors.HEADER};
        }
        .ant-tabs > .ant-tabs-nav {
          background: ${colors.SECONDARY_300} !important;
        }
        .ant-tabs-tab {
          padding: 8px 16px !important;
        }
        .ant-tabs-tab .ant-tabs-tab-btn {
          color: ${colors.NEUTRAL_100} !important;
          font-family: Inter, sans-serif !important;
          font-size: 16px !important;
          font-weight: 400 !important;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: ${colors.PRIMARY} !important;
          font-weight: 600 !important;
        }
        .ant-tabs-tab-disabled .ant-tabs-tab-btn {
          color: ${colors.NEUTRAL_100} !important;
        }
        .ant-tabs-ink-bar {
          background: ${colors.PRIMARY} !important;
          height: 1px !important;
        }
        .ant-tabs-nav::before {
        }
        .ant-tabs-content-holder {
          padding-top: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .ant-tabs-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .ant-tabs-tabpane-active {
          flex: 1;
          display: flex !important;
          flex-direction: column;
        }
        .ant-upload-wrapper,
        .ant-upload-drag {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .ant-upload-drag .ant-upload {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .ant-upload-drag .ant-upload-btn {
          flex: 1;
          display: flex !important;
          flex-direction: column;
          justify-content: center;
        }
      `}
      width={isMobile ? '100%' : 600}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      styles={modalStyles}
      maskClosable={false}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          flex: 1;
        `}
      >
        <Text variant='paragraphMedium' weight='medium' color={colors.WHITE}>
          Choose one of the options below to add your video content:
        </Text>
        <Tabs
          defaultActiveKey={
            !isHostedOnS3 && isEditing
              ? VIDEO_TYPE.YOUTUBE_URL
              : VIDEO_TYPE.VIDEO_UPLOAD
          }
          items={tabs}
          onChange={onChangeTabs}
          style={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
          tabBarStyle={{
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            borderBottomColor: colors.SECONDARY_75,
            marginBottom: 0,
          }}
        />
        {error ? (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        ) : null}
      </div>
    </Modal>
  )
}

export default UploadVideoModal
